import React from 'react';
import {
  Checkbox,
  Dialog,
  IconButton, InfoSignIcon,
  ListIcon,
  Pane,
  Position,
  RadioGroup,
  Strong,
  Tab,
  Tablist,
  Text,
  Tooltip,
} from 'evergreen-ui';
import { createUseStyles } from 'react-jss';
import { format } from 'date-fns';
import { useUrlParams } from 'Utils/useUrlParams';
import { TimeseriesContext } from './Timeseries.Context';
import { CheckboxList } from '../CheckboxList2';
import { TimeseriesPanelContext } from './Timeseries.Panel.Context';

const useStyles = createUseStyles({
  displayContents: {
    '& > div': {
      display: 'contents',
    },
  },
});

export default function MeasureSelector() {
  const classes = useStyles();
  const {
    selectedMeteocodeFilter,
    selectedMeteocodes,
    selectedPrograms,
  } = React.useContext(TimeseriesPanelContext);
  const [, updateParams] = useUrlParams();
  const [isShowDialog, setIsShowDialog] = React.useState(false);

  const onConfirm = () => {
    const params = {
      meteocodeFilter: selectedMeteocodeFilter,
    };

    if (selectedMeteocodes.length > 0) {
      params.meteocodes = selectedMeteocodes.map((meteocode) => meteocode.selectedName);
    } else {
      params.meteocodes = [];
    }
    updateParams(params);
    setIsShowDialog(false);
  };

  return (
    <>
      <Tooltip content="Программы и Метеокоды" position={Position.TOP}>
        <IconButton icon={ListIcon} onClick={() => setIsShowDialog(true)} />
      </Tooltip>
      {isShowDialog && (
        <Dialog
          isShown
          shouldCloseOnOverlayClick={false}
          onCloseComplete={() => setIsShowDialog(false)}
          onConfirm={onConfirm}
          hasCancel={false}
          intent="success"
          confirmLabel="Применить"
          title="Выбор измерений"
          width="75%"
          contentContainerProps={{
            className: classes.displayContents,
          }}
        >
          <MeasureSelectorDialog />
        </Dialog>
      )}
    </>
  );
}

function MeasureSelectorDialog() {
  const [tmpTab, setTmpTab] = React.useState(0);
  const {allMeteocodes} = React.useContext(TimeseriesContext);
  const {
    selectedMeteocodes,
    setSelectedMeteocodes,
  } = React.useContext(TimeseriesPanelContext);
  const [tab, setTab] = React.useState(0);
  const [isShowWarningDialog, setIsShowWarningDialog] = React.useState(false);

  const onConfirmDialog = () => {
    setSelectedMeteocodes([]);
    setTab(tmpTab);
    setIsShowWarningDialog(false);
  };

  const filteredByFirstTimeReceived = React.useMemo(
    () => allMeteocodes.filter(
        (meteocode) => {
          return meteocode.firstReceivedTimeUTC !== null;
        }),
    [allMeteocodes]
  );

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" rowGap=".5rem" overflow="hidden">
        <Pane>{`Метеокоды (${selectedMeteocodes.length}/${filteredByFirstTimeReceived.length})`}</Pane>
      <MeteocodeSelector />
      {
        isShowWarningDialog && (
          <Dialog
            isShown
            title="Внимание"
            shouldCloseOnOverlayClick={false}
            onCloseComplete={() => setIsShowWarningDialog(false)}
            onConfirm={onConfirmDialog}
            confirmLabel="Подтвердить"
            intent="success"
            cancelLabel="Отмена"
          >
            При пререключении вкладки выбранные параметры сбросятся. Продолжить?
          </Dialog>
        )
      }
    </Pane>
  );
}

function MeteoCodeLabelDefault({ value }) {
  return (
    <>
      <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".5rem" alignItems="center">
        {value.firstReceivedTimeUTC && (
          <Tooltip
            statelessProps={{
              textAlign: 'center',
            }}
            content={`Дата первого поступления ${format(new Date(value.firstReceivedTimeUTC), 'yyyy.MM.dd HH:mm:ss')}`}
          >
            <InfoSignIcon />
          </Tooltip>
        )}
        <Strong>{`[${value.meteoCode}] `}</Strong>
      </Pane>
      <Text>{value.meteoCodeName}</Text>
      <br />
      {value.placeName && <Strong>{value.placeName}</Strong>}
      {value.entityId && <Strong>{`(${value.entityName})`}</Strong>}
    </>
  );
}

const MeteoCodeLabel = React.memo(MeteoCodeLabelDefault);
MeteoCodeLabel.displayName = 'MeteoCodeLabel';

function MeteocodeSelector() {
  const { allMeteocodes } = React.useContext(TimeseriesContext);
  const {
    selectedMeteocodeFilter, setSelectedMeteocodeFilter, selectedMeteocodes, setSelectedMeteocodes,
  } = React.useContext(TimeseriesPanelContext);

  const [isShowFactMeteocodes, setIsShowFactMeteocodes] = React.useState(true);

  const options = React.useMemo(() => [
    {
      value: 'place',
      label: 'Месту расположения',
    },
    {
      value: 'entity',
      label: 'Объекту паспорта станции',
    },
  ], []);

  const filteredByFirstTimeReceived = React.useMemo(() => allMeteocodes.filter((meteocode) => {
    if (selectedMeteocodeFilter === 'entity' && !meteocode.placeName) {
      return true;
    }
    if (selectedMeteocodeFilter === 'place' && !meteocode.entityId) {
      return true;
    }
    return false;
  }).filter((meteocode) => {
    if (isShowFactMeteocodes) {
      return meteocode.firstReceivedTimeUTC !== null;
    }
    return true;
  }), [allMeteocodes, isShowFactMeteocodes, selectedMeteocodeFilter]);

  const onChangeMeteocodeFilter = (e) => {
    const { value } = e.target;
    setSelectedMeteocodes([]);
    setSelectedMeteocodeFilter(value);
  };

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" overflow="hidden">
      <Pane display="grid" gridTemplateColumns="max-content 1fr" columnGap=".5rem">
        <Strong>Группировка по:</Strong>
        <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".5rem">
          {options.map((option) => (
            <Pane key={option.value} display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".2rem" alignItems="center">
              <label>
                <input style={{ marginRight: '5px' }} type="radio" value={option.value} checked={option.value === selectedMeteocodeFilter} onChange={onChangeMeteocodeFilter} />
                <Text>{option.label}</Text>
              </label>
            </Pane>
          ))}
        </Pane>
      </Pane>
      <CheckboxList
        values={filteredByFirstTimeReceived}
        checkedValues={selectedMeteocodes}
        setCheckedValues={setSelectedMeteocodes}
        checkProp="selectedName"
        labelProp={['meteoCodeName', 'meteoCode', 'entityName', 'placeName']}
        LabelElement={MeteoCodeLabel}
      />
    </Pane>
  );
}
